import { FunctionComponent, KeyboardEvent, useState } from 'react';
import { Box, Flex, IconButton, Input, Text, useToken } from '@chakra-ui/react';
import { Reorder, useDragControls } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';

const PREFIX = 'pages.application_roster_group.components.roster_group_position_order_field';

type PositionOrderFieldProps = {
  value: string[];
  onChange: (next: string[]) => void;
};

export const PositionOrderField: FunctionComponent<PositionOrderFieldProps> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');

  const handleAdd = () => {
    const label = draft.trim();
    if (!label || value.includes(label)) return;
    onChange([...value, label]);
    setDraft('');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAdd();
    }
  };

  const handleRemove = (label: string) => {
    onChange(value.filter((item) => item !== label));
  };

  return (
    <Box>
      <Text fontSize='sm' fontWeight='medium' mb={1}>
        {t(`${PREFIX}.label`)}
      </Text>
      <Text fontSize='xs' color='gray.500' mb={2}>
        {t(`${PREFIX}.hint`)}
      </Text>

      <Flex gap={2} mb={3}>
        <Input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t(`${PREFIX}.placeholder`)}
          maxLength={50}
        />
        <IconButton
          aria-label={t(`${PREFIX}.add`)}
          icon={<ChakraFeatherIcon name='plus' boxSize='16px' />}
          onClick={handleAdd}
          isDisabled={!draft.trim()}
        />
      </Flex>

      {value.length === 0 ? (
        <Text fontSize='xs' color='gray.400'>
          {t(`${PREFIX}.empty`)}
        </Text>
      ) : (
        <Reorder.Group as='div' axis='y' values={value} onReorder={onChange} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {value.map((label) => (
            <PositionOrderItem key={label} label={label} onRemove={() => handleRemove(label)} />
          ))}
        </Reorder.Group>
      )}
    </Box>
  );
};

type PositionOrderItemProps = {
  label: string;
  onRemove: () => void;
};

const PositionOrderItem: FunctionComponent<PositionOrderItemProps> = ({ label, onRemove }) => {
  const dragControls = useDragControls();
  const [bg, borderColor] = useToken('colors', ['gray.50', 'gray.200']);

  return (
    <Reorder.Item
      value={label}
      dragListener={false}
      dragControls={dragControls}
      style={{
        background: bg,
        border: `1px solid ${borderColor}`,
        borderRadius: 6,
        padding: '6px 10px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        touchAction: 'none',
      }}
    >
      <Box
        role='button'
        aria-label='drag'
        cursor='grab'
        color='gray.400'
        _hover={{ color: 'brand.500' }}
        display='inline-flex'
        alignItems='center'
        style={{ touchAction: 'none' }}
        onPointerDown={(event) => dragControls.start(event)}
      >
        <ChakraFeatherIcon name='menu' boxSize='14px' />
      </Box>
      <Text flex={1} fontSize='sm'>
        {label}
      </Text>
      <IconButton
        aria-label='remove'
        size='xs'
        variant='ghost'
        icon={<ChakraFeatherIcon name='x' boxSize='14px' />}
        onClick={onRemove}
      />
    </Reorder.Item>
  );
};
